import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import RecordApproval from "@/components/RecordApproval";
import { staffTitles } from "@/lib/staffTitles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CouncilRecordPrint, { useCouncilPrint } from "@/components/CouncilRecordPrint";
import { choiceValues, choiceText, MeetingSelect, MultiChoice, MEETING_TIMES, MEETING_LOCATIONS, MEETING_STATUSES } from "@/components/MeetingChoices";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarDays, Check, ClipboardList, Clock3, GraduationCap, Pencil, Plus, Users, UserRound, Trash2, Printer,
} from "lucide-react";

const MEMBER_TYPE = "guidance_committee_member";
const MEETING_TYPE = "guidance_committee_meeting";
const ROLE_OPTIONS = [
  { value: "chair", label: "رئيس اللجنة — وكيل شؤون الطلاب" },
  { value: "rapporteur", label: "المقرر — المرشد الطلابي" },
  { value: "teacher", label: "عضو — معلم" },
  { value: "administrator", label: "عضو — إداري" },
];
const EMPTY_MEMBER = { name: "", role: "teacher" };
const EMPTY_MEETING = { date: "", time: "", location: "", agenda: [], decisions: [], attendeeIds: [], status: "scheduled" };
const EMPTY_OTHER = { agendaOtherSelected: false, agendaOtherText: "", decisionsOtherSelected: false, decisionsOtherText: "" };
const AGENDA_OPTIONS = ["متابعة الحالات الطلابية", "الانضباط المدرسي", "الغياب والتأخر", "البرامج الإرشادية", "التحصيل الدراسي", "السلوك الإيجابي", "التواصل مع أولياء الأمور", "متابعة توصيات الاجتماع السابق"];
const DECISION_OPTIONS = ["تنفيذ برنامج إرشادي", "متابعة الحالة مع المرشد", "التواصل مع ولي الأمر", "إحالة الموضوع لإدارة المدرسة", "تكليف الأعضاء بالمتابعة", "متابعة التوصية في الاجتماع القادم", "تمت المعالجة"];

function roleLabel(role, gender) {
  if (role === "rapporteur" && gender) return `${gender === "female" ? "المقررة" : "المقرر"} — ${staffTitles(gender).counselor}`;
  return ROLE_OPTIONS.find((option) => option.value === role)?.label || "عضو";
}

function dateLabel(date) {
  if (!date) return "دون تاريخ";
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? date
    : parsed.toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric" });
}

function CommitteeChoices({ label, field, options, form, setForm }) {
  const selectedKey = `${field}OtherSelected`;
  const textKey = `${field}OtherText`;
  const id = `committee-${field}-other`;
  return (
    <div className="space-y-2">
      <MultiChoice label={label} options={[...new Set([...options, ...form[field]])].map((value) => ({ value, label: value }))} value={form[field]} onChange={(values) => setForm((current) => ({ ...current, [field]: values }))} />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form[selectedKey]} onChange={(event) => setForm((current) => ({ ...current, [selectedKey]: event.target.checked }))} />
        أخرى
      </label>
      {form[selectedKey] && (
        <div className="space-y-2">
          <Label htmlFor={id}>{label} — أخرى</Label>
          <Input id={id} value={form[textKey]} onChange={(event) => setForm((current) => ({ ...current, [textKey]: event.target.value }))} required />
        </div>
      )}
    </div>
  );
}

function MemberDialog({ open, onOpenChange, member, onSave, counselor, members }) {
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(member ? { ...member.data, name: member.data.name || "", role: member.data.role || "teacher" } : EMPTY_MEMBER);
  }, [member, open]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form, name: form.name.trim() }, member?.id);
      onOpenChange(false);
    } catch {
      // Keep the dialog open so the user can correct or retry after a failed save.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{member ? "تعديل بيانات العضو" : "إضافة عضو للجنة"}</DialogTitle>
          <DialogDescription>أدخل اسم عضو الفريق وحدد مسؤوليته في اللجنة.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            {!member && counselor?.id && counselor?.name && (
              <Button type="button" variant="outline" disabled={members.some((entry) => entry.data.counselor_id === counselor.id || entry.data.name?.trim() === counselor.name.trim())}
                onClick={() => setForm({ name: counselor.name, role: "rapporteur", counselor_id: counselor.id })} data-testid="committee-add-counselor">
                إضافة {staffTitles(counselor.gender).counselor} {counselor.gender === "female" ? "الحالية مقررةً" : "الحالي مقررًا"} — {counselor.name}
              </Button>
            )}
            <Label htmlFor="committee-member-name">اسم العضو</Label>
            <Input id="committee-member-name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} required autoFocus disabled={Boolean(form.counselor_id)} />
          </div>
          <div className="space-y-2">
            <Label>الدور في اللجنة</Label>
            <Select disabled={Boolean(form.counselor_id)} value={form.role} onValueChange={(role) => setForm((current) => ({ ...current, role }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{roleLabel(option.value, counselor?.gender)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
            <Button type="submit" disabled={saving || !form.name.trim()} className="bg-[#27624d] hover:bg-[#1f503e]">
              {saving ? "جاري الحفظ..." : "حفظ العضو"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MeetingDialog({ open, onOpenChange, meeting, members, counselorGender, onSave }) {
  const [form, setForm] = useState({ ...EMPTY_MEETING, ...EMPTY_OTHER });
  const [saving, setSaving] = useState(false);
  const availableMembers = [
    ...members,
    ...(meeting?.data?.attendees || [])
      .filter((attendee) => !members.some((member) => member.id === attendee.member_id))
      .map((attendee) => ({ id: attendee.member_id, data: { name: attendee.name, role: attendee.role } })),
  ];

  useEffect(() => {
    if (!open) return;
    setForm(meeting ? {
      date: meeting.data.date || "",
      time: meeting.data.time || "",
      location: meeting.data.location || "",
      agenda: choiceValues(meeting.data.agenda).filter((value) => value !== meeting.data.agenda_other),
      decisions: choiceValues(meeting.data.decisions).filter((value) => value !== meeting.data.decisions_other),
      agendaOtherSelected: Boolean(meeting.data.agenda_other),
      agendaOtherText: meeting.data.agenda_other || "",
      decisionsOtherSelected: Boolean(meeting.data.decisions_other),
      decisionsOtherText: meeting.data.decisions_other || "",
      status: meeting.data.status || "scheduled",
      attendeeIds: meeting.data.attendees?.map((attendee) => attendee.member_id) || [],
    } : { ...EMPTY_MEETING, ...EMPTY_OTHER });
  }, [meeting, open]);

  const toggleAttendee = (memberId) => {
    setForm((current) => ({
      ...current,
      attendeeIds: current.attendeeIds.includes(memberId)
        ? current.attendeeIds.filter((id) => id !== memberId)
        : [...current.attendeeIds, memberId],
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if ((form.agendaOtherSelected && !form.agendaOtherText.trim()) || (form.decisionsOtherSelected && !form.decisionsOtherText.trim())) {
      toast.error("أدخل النص عند اختيار «أخرى».");
      return;
    }
    setSaving(true);
    try {
      const attendees = availableMembers
        .filter((member) => form.attendeeIds.includes(member.id))
        .map((member) => ({ member_id: member.id, name: member.data.name, role: member.data.role, role_label: roleLabel(member.data.role, counselorGender) }));
      const { agendaOtherSelected, agendaOtherText, decisionsOtherSelected, decisionsOtherText, ...fields } = form;
      const agendaOther = agendaOtherSelected ? agendaOtherText.trim() : "";
      const decisionsOther = decisionsOtherSelected ? decisionsOtherText.trim() : "";
      await onSave({
        ...fields,
        agenda: [...new Set([...form.agenda, ...(agendaOther ? [agendaOther] : [])])],
        decisions: [...new Set([...form.decisions, ...(decisionsOther ? [decisionsOther] : [])])],
        agenda_other: agendaOther,
        decisions_other: decisionsOther,
        attendees,
      }, meeting?.id);
      onOpenChange(false);
    } catch {
      // Keep the dialog open so the user can correct or retry after a failed save.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{meeting ? "تعديل محضر الاجتماع" : "توثيق اجتماع جديد"}</DialogTitle>
          <DialogDescription>سجل موعد الاجتماع ومحاوره وما انتهى إليه من قرارات.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="committee-meeting-date">تاريخ الاجتماع</Label>
              <Input id="committee-meeting-date" type="date" value={form.date} onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))} required />
            </div>
            <MeetingSelect label="الوقت" value={form.time} options={MEETING_TIMES} onChange={(time) => setForm((current) => ({ ...current, time }))} />
            <MeetingSelect label="مكان الاجتماع" value={form.location} options={MEETING_LOCATIONS} onChange={(location) => setForm((current) => ({ ...current, location }))} />
            <div className="space-y-2">
              <Label>حالة الاجتماع</Label>
              <Select value={form.status} onValueChange={(status) => setForm((current) => ({ ...current, status }))}>
                <SelectTrigger aria-label="حالة الاجتماع"><SelectValue /></SelectTrigger>
                <SelectContent>{MEETING_STATUSES.map((status) => <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <CommitteeChoices label="موضوعات ومحاور الاجتماع" field="agenda" options={AGENDA_OPTIONS} form={form} setForm={setForm} />
          <CommitteeChoices label="القرارات والتوصيات" field="decisions" options={DECISION_OPTIONS} form={form} setForm={setForm} />
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">الحضور</legend>
            {availableMembers.length ? (
              <div className="grid gap-2 rounded-xl border border-[#e3eae5] bg-[#f7faf8] p-3 sm:grid-cols-2">
                {availableMembers.map((member) => {
                  const checked = form.attendeeIds.includes(member.id);
                  return (
                    <label key={member.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${checked ? "border-[#4a8a69] bg-white text-[#245b43]" : "border-transparent text-[#56655c] hover:bg-white"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleAttendee(member.id)} className="accent-[#27624d]" />
                      <span className="min-w-0 flex-1 truncate">{member.data.name}</span>
                      <span className="text-[11px] text-[#7b8981]">{roleLabel(member.data.role, counselorGender).split(" — ")[0]}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="rounded-xl bg-[#f7faf8] p-3 text-sm text-[#68776e]">أضف أعضاء اللجنة أولاً لتحديد الحضور.</p>
            )}
          </fieldset>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
            <Button type="submit" disabled={saving || (!form.agenda.length && !(form.agendaOtherSelected && form.agendaOtherText.trim())) || (form.agendaOtherSelected && !form.agendaOtherText.trim()) || (form.decisionsOtherSelected && !form.decisionsOtherText.trim())} className="bg-[#27624d] hover:bg-[#1f503e]">
              {saving ? "جاري الحفظ..." : "حفظ المحضر"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function GuidanceCommittee({ readOnly = false, counselorId = null, counselor = null }) {
  const { user } = useAuth();
  const owner = counselor || (counselorId ? null : user);
  const printing = useCouncilPrint();
  const [members, setMembers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const isReadOnly = readOnly || user?.role !== "counselor" || (Boolean(counselorId) && counselorId !== user.id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = counselorId ? { counselor_id: counselorId } : {};
      const [memberResponse, meetingResponse] = await Promise.all([
        api.get("/records", { params: { ...params, type: MEMBER_TYPE } }),
        api.get("/records", { params: { ...params, type: MEETING_TYPE } }),
      ]);
      setMembers(memberResponse.data);
      setMeetings(meetingResponse.data.sort((left, right) => String(right.data.date).localeCompare(String(left.data.date))));
      return true;
    } catch (error) {
      toast.error(formatErr(error));
      return false;
    } finally {
      setLoading(false);
    }
  }, [counselorId]);

  useEffect(() => { load(); }, [load]);

  const saveRecord = async (type, data, id) => {
    if (type === MEMBER_TYPE && data.role === "rapporteur" && members.some((member) => member.id !== id && (
      (data.counselor_id && member.data.counselor_id === data.counselor_id) ||
      ((data.counselor_id || member.data.role === "rapporteur") && member.data.name?.trim() === data.name.trim())
    ))) {
      const error = new Error("المرشد مسجل بالفعل مقررًا في اللجنة.");
      toast.error(error.message);
      throw error;
    }
    try {
      if (id) await api.put(`/records/${id}`, { data });
      else await api.post("/records", { type, data });
      if (await load()) toast.success(id ? "تم تحديث السجل" : "تمت الإضافة بنجاح");
    } catch (error) {
      toast.error(formatErr(error));
      throw error;
    }
  };

  const removeRecord = async (record, label) => {
    if (!window.confirm(`هل تريد حذف ${label}؟`)) return;
    try {
      await api.delete(`/records/${record.id}`);
      if (await load()) toast.success("تم الحذف");
    } catch (error) {
      toast.error(formatErr(error));
    }
  };

  const roleCounts = useMemo(() => members.reduce((counts, member) => {
    counts[member.data.role] = (counts[member.data.role] || 0) + 1;
    return counts;
  }, {}), [members]);

  return (
    <div className="space-y-6" data-testid="guidance-committee-page">
      <RecordApproval section="guidance_committee" counselorId={counselorId} counselorGender={owner?.gender} contentCount={members.length + meetings.length} />
      <Button variant="outline" disabled={loading} onClick={() => printing.print()} data-testid="print-committee"><Printer className="me-2 h-4 w-4" /> طباعة السجل والمحاضر A4</Button>
      <section className="relative overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-[#153d33] via-[#205844] to-[#317255] p-6 text-white shadow-[0_20px_50px_rgba(27,77,55,0.18)] sm:p-8">
        <div className="pointer-events-none absolute -left-12 -top-24 h-64 w-64 rounded-full border border-white/10 shadow-[0_0_0_28px_rgba(255,255,255,0.035),0_0_0_58px_rgba(255,255,255,0.025)]" />
        <div className="relative flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-[#d5edbd]">
              <GraduationCap className="h-4 w-4" /> العمل الإرشادي المدرسي
            </div>
            <h1 className="text-2xl font-bold sm:text-3xl" style={{ fontFamily: "IBM Plex Sans Arabic" }}>لجنة التوجيه والإرشاد</h1>
            <p className="mt-2 max-w-xl text-sm leading-7 text-white/70">إدارة فريق اللجنة وتوثيق اجتماعاتها وقراراتها في سجل واحد منظم.</p>
          </div>
          {!isReadOnly && (
            <Button onClick={() => { setEditingMeeting(null); setMeetingDialogOpen(true); }} className="shrink-0 rounded-xl bg-[#d1edaa] text-[#183d30] hover:bg-[#c1e395]" data-testid="add-committee-meeting">
              <Plus className="me-2 h-4 w-4" /> توثيق اجتماع
            </Button>
          )}
        </div>
        <div className="relative mt-7 grid grid-cols-2 gap-3 sm:max-w-md">
          <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
            <div className="flex items-center gap-2 text-xs text-white/65"><Users className="h-4 w-4" /> أعضاء الفريق</div>
            <div className="mt-2 text-3xl font-bold">{members.length}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
            <div className="flex items-center gap-2 text-xs text-white/65"><ClipboardList className="h-4 w-4" /> الاجتماعات الموثقة</div>
            <div className="mt-2 text-3xl font-bold">{meetings.length}</div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#e5ebe6] bg-white shadow-[0_10px_30px_rgba(24,48,36,0.035)]">
        <div className="flex flex-col gap-4 border-b border-[#edf0ed] p-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#eaf3ed] text-[#27624d]"><Users className="h-5 w-5" /></span>
            <div>
              <h2 className="text-lg font-bold text-[#1e3429]">فريق اللجنة</h2>
              <p className="mt-1 text-sm text-[#718078]">حدد الرئيس والمقرر وأضف بقية الأعضاء.</p>
            </div>
          </div>
          {!isReadOnly && (
            <Button variant="outline" onClick={() => { setEditingMember(null); setMemberDialogOpen(true); }} className="rounded-xl border-[#dce7de] text-[#27624d] hover:bg-[#f3f8f4]" data-testid="add-committee-member">
              <Plus className="me-2 h-4 w-4" /> إضافة عضو
            </Button>
          )}
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#718078]">جاري تحميل بيانات اللجنة...</div>
        ) : members.length ? (
          <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-3">
            {members.map((member) => (
              <article key={member.id} className="group rounded-2xl border border-[#e8eee9] bg-[#fbfcfb] p-4 transition hover:border-[#c8ddce] hover:bg-white hover:shadow-[0_10px_25px_rgba(24,70,44,0.06)]" data-testid={`committee-member-${member.id}`}>
                <div className="flex items-start gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-[#367353] shadow-sm ring-1 ring-[#e9eee9]"><UserRound className="h-5 w-5" /></span>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold text-[#25372d]">{member.data.name}</h3>
                    <span className="mt-1.5 inline-flex rounded-full bg-[#eaf3ed] px-2.5 py-1 text-[11px] font-medium text-[#31684c]">{roleLabel(member.data.role, owner?.gender)}</span>
                  </div>
                  {!isReadOnly && (
                    <div className="flex gap-1 opacity-70 transition group-hover:opacity-100">
                      <Button size="icon" variant="ghost" aria-label={`تعديل ${member.data.name}`} onClick={() => { setEditingMember(member); setMemberDialogOpen(true); }} className="h-8 w-8 text-[#567064]"><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" aria-label={`حذف ${member.data.name}`} onClick={() => removeRecord(member, `العضو ${member.data.name}`)} className="h-8 w-8 text-[#a24d4d]"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mx-4 my-5 rounded-2xl border border-dashed border-[#d8e4db] bg-[#f8fbf8] px-5 py-9 text-center sm:mx-5">
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-white text-[#59806a] shadow-sm"><Users className="h-5 w-5" /></span>
            <h3 className="mt-3 font-semibold text-[#31443a]">ابدأ بتكوين فريق اللجنة</h3>
            <p className="mt-1 text-sm text-[#78857d]">أضف {staffTitles(owner?.gender).deputy} {owner?.gender === "female" ? "رئيسةً" : "رئيساً"}، ثم {staffTitles(owner?.gender).counselor} {owner?.gender === "female" ? "مقررةً" : "مقرراً"}، وبقية الأعضاء.</p>
          </div>
        )}
        {!loading && members.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-[#edf0ed] px-5 py-3 text-xs text-[#718078]">
            {ROLE_OPTIONS.map((role) => roleCounts[role.value] ? <span key={role.value} className="rounded-full bg-[#f2f6f3] px-3 py-1.5">{roleLabel(role.value, owner?.gender).split(" — ")[0]} <b className="text-[#315f47]">{roleCounts[role.value]}</b></span> : null)}
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#e5ebe6] bg-white shadow-[0_10px_30px_rgba(24,48,36,0.035)]">
        <div className="flex items-start gap-3 border-b border-[#edf0ed] p-5 sm:px-6">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#f7f0df] text-[#a27b2c]"><CalendarDays className="h-5 w-5" /></span>
          <div>
            <h2 className="text-lg font-bold text-[#1e3429]">محاضر الاجتماعات</h2>
            <p className="mt-1 text-sm text-[#718078]">تابع الموضوعات والحضور والقرارات والتوصيات لكل اجتماع.</p>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#718078]">جاري تحميل المحاضر...</div>
        ) : meetings.length ? (
          <div className="divide-y divide-[#edf0ed]">
            {meetings.map((meeting, index) => (
              <article key={meeting.id} className="grid gap-4 p-5 sm:grid-cols-[9rem_1fr] sm:px-6" data-testid={`committee-meeting-${meeting.id}`}>
                <div className="flex items-center gap-3 sm:block">
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#f3f7f3] text-[#367353] sm:mb-2"><CalendarDays className="h-5 w-5" /></div>
                  <div>
                    <div className="text-sm font-bold text-[#30453a]">{dateLabel(meeting.data.date)}</div>
                    {meeting.data.time && <div className="mt-1 flex items-center gap-1 text-xs text-[#839087]"><Clock3 className="h-3 w-3" />{meeting.data.time}</div>}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-[#edf5ef] px-2.5 py-1 text-[11px] font-semibold text-[#397253]">اجتماع {meetings.length - index}</span>
                      {meeting.data.location && <span className="text-xs text-[#819087]">{meeting.data.location}</span>}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => printing.print(meeting)} data-testid={`print-committee-meeting-${meeting.id}`}><Printer className="me-2 h-4 w-4" /> طباعة المحضر</Button>
                    {!isReadOnly && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEditingMeeting(meeting); setMeetingDialogOpen(true); }} className="h-8 rounded-lg text-[#557064]"><Pencil className="me-1.5 h-3.5 w-3.5" /> تعديل</Button>
                        <Button size="icon" variant="ghost" aria-label="حذف المحضر" onClick={() => removeRecord(meeting, "محضر الاجتماع")} className="h-8 w-8 text-[#a24d4d]"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    <div className="rounded-xl bg-[#f8faf8] p-3.5">
                      <div className="mb-1.5 text-xs font-bold text-[#53675b]">الموضوعات والمحاور</div>
                      <p className="whitespace-pre-wrap text-sm leading-6 text-[#42544a]">{choiceText(meeting.data.agenda)}</p>
                    </div>
                    <div className="rounded-xl bg-[#fbf8ef] p-3.5">
                      <div className="mb-1.5 text-xs font-bold text-[#876d38]">القرارات والتوصيات</div>
                      <p className="whitespace-pre-wrap text-sm leading-6 text-[#625840]">{choiceText(meeting.data.decisions) || "لم تسجل قرارات أو توصيات."}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-[#68776e]">الحضور</span>
                    {meeting.data.attendees?.length ? meeting.data.attendees.map((attendee) => (
                      <span key={attendee.member_id} className="inline-flex items-center gap-1 rounded-full border border-[#e7ece8] bg-white px-2.5 py-1 text-xs text-[#5a6c61]">
                        <Check className="h-3 w-3 text-[#48805d]" /> {attendee.name}
                      </span>
                    )) : <span className="text-xs text-[#87928b]">لم يسجل الحضور</span>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mx-4 my-5 rounded-2xl border border-dashed border-[#e7e0cc] bg-[#fcfbf7] px-5 py-9 text-center sm:mx-5">
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-white text-[#a27b2c] shadow-sm"><ClipboardList className="h-5 w-5" /></span>
            <h3 className="mt-3 font-semibold text-[#453e2f]">لا توجد اجتماعات موثقة بعد</h3>
            <p className="mt-1 text-sm text-[#817b6e]">ابدأ بإضافة محضر يتضمن التاريخ والموضوعات والحضور والقرارات.</p>
          </div>
        )}
      </section>

      <CouncilRecordPrint request={printing.request} onFinish={printing.finish} title="لجنة التوجيه والإرشاد" schoolKey={owner?.school_key} schoolName={owner?.school_name} counselorName={owner?.name} counselorId={counselorId}
        counselorGender={owner?.gender}
        members={members.map((member) => ({ id: member.id, name: member.data.name, detail: roleLabel(member.data.role, owner?.gender) }))}
        meetings={meetings.map((meeting) => ({ ...meeting, data: { ...meeting.data, attendees: (meeting.data.attendees || []).map((attendee) => ({ ...attendee, role_label: attendee.role === "rapporteur" ? roleLabel(attendee.role, owner?.gender) : attendee.role_label || roleLabel(attendee.role) })) } }))} />
      <MemberDialog
        open={memberDialogOpen}
        onOpenChange={setMemberDialogOpen}
        member={editingMember}
        counselor={owner}
        members={members}
        onSave={(data, id) => saveRecord(MEMBER_TYPE, data, id)}
      />
      <MeetingDialog
        open={meetingDialogOpen}
        onOpenChange={setMeetingDialogOpen}
        meeting={editingMeeting}
        members={members}
        counselorGender={owner?.gender}
        onSave={(data, id) => saveRecord(MEETING_TYPE, data, id)}
      />
    </div>
  );
}
